import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'
import { createBuildingRipple, rippleRadiusForBuilding } from './buildingRipple'
import { createBuildingDetailView } from './buildingDetailView'
import { createLights, TONE_MAPPING_EXPOSURE, NIGHT_MODE, SHADOWS, fitShadowCamera } from './sceneLights'
import { createEnvironment } from './environment'
import { createDuskEnvironment } from './skyEnvironment'
import { wgs2gcj } from './geo'
import { LEADER_LINE_STYLE } from './loadCityTiles'
import {
  createRoadLabels,
  ROAD_LABEL_SCREEN_HEIGHT_PX,
  ROAD_LABEL_MIN_ZOOM,
  ROAD_LABEL_CHAR_STEP,
} from './roadLabels'

/**
 * 纯 three 的城市场景视图（**不再依赖高德底图**）。
 *
 * 与旧的 amapScene.js 的关系：那份是「高德 3D 底图 + GLCustomLayer 叠 three」，
 * 相机、渲染时机、坐标全部由高德托管。现在画面里的地面、街道、周边城市都已经
 * 由 OSM 数据自建（见 art/build_context.py、art/build_roads.py），高德的视觉贡献归零，
 * 而共享它那个 WebGL 上下文带来的一整串约束还在持续咬人：
 *   - 上下文是 WebGL1（Bloom 的 vec4(vec3) 编不过就是它害的）
 *   - autoClear 必须 false、clearAlpha 必须 0、每帧两次 resetState() 交接状态
 *   - Bloom 会把底图盖成黑底，得改模糊着色器的 alpha 传递
 *   - 标牌尺寸不能用 renderer.domElement 的高度（那是个 150px 的游离画布）
 * 拆掉之后这些全部消失，渲染器可以自建、直接要 WebGL2、用不透明背景。
 *
 * 坐标系（**和旧实现完全一致，模型/标牌/环境层代码因此不用改**）：
 *   世界系 Z-up：X = 东 / Y = 北 / Z = 上，单位米，原点 = 模型包围盒中心的地面点。
 *   模型组是 Y-up，外面包一层 rotX(+90°) 转到这个 Z-up 世界。
 *
 * zoom 语义：为了保住历史上按高德级别调过的那些常量（DEFAULT_ZOOM / LABEL_MIN_ZOOM /
 * FLY_ZOOM），这里用 web-mercator 的「每像素米数」把 zoom 映射成相机距离，
 * 数值和在高德下量到的观感一致，不是随便编的系数。
 */

// ============ 视角 ============
/**
 * 默认缩放级别。18.3 是高德时代按"23 栋楼占满画面"调的，那时园区保留圈只有 400m；
 * 现在绿地扩到 880m、还带周边城市，18.3 一进来就是怼脸特写，看不到整体。
 * 16.8 比 17.3 远 1.41 倍（800px 容器高下相机离圆心 ≈1.0km），进场先看全岛和周边城市层次。
 */
export const DEFAULT_ZOOM = 16.8
/**
 * 默认俯仰角（度）—— ⚠️ 语义是**相机相对地平线的仰角**：
 *   0 = 平视（相机贴地看过去），90 = 正俯视（相机在正上方）。
 * 别按高德那套"pitch 0 = 俯视"来理解，两者是反的：高德的 pitch 55
 * 相当于这里的 35。33 仍能看出屋面布局，压到 26 让立面和天际线成为主角。
 */
export const DEFAULT_PITCH = 26
/**
 * 默认方位角（度，指相机所在方位）：0 = 相机在注视点正北、镜头朝南，180 = 在正南、朝北。
 */
export const DEFAULT_AZIMUTH = 180
/** 相机 fov。高德 3D 默认在 40~55 之间，这里取 50 让体块透视不那么夸张 */
export const CAMERA_FOV = 50
/** 缩放区间（对应相机最近/最远距离）。下限 16.5 = 相机离圆心约 1.2km：
 * 再退就把整个绿岛缩成一块小饼干、周边城市糊成一片灰，参考图那种"城包着镇"的
 * 层次就没了。要临时看全景改这里，别在运行时硬拉。 */
export const ZOOM_RANGE = [16.5, 19.6]
/**
 * 园区圆心（世界米，X=东 / Y=北）：23 栋编号楼占地圆的圆心。
 * ⚠️ 必须和 art/build_roads.py 的 CAMPUS_CENTER、art/build_context.py 的 CAMPUS_KEEP
 * 以及 environment.js 的 GROUND_FADE_CENTER 保持同一个点 —— 树、路、地台渐隐、
 * 背景楼避让全是围绕它铺的，相机另取一个中心就会看到"绿地跑偏"。
 * 数值由 art/building-layout.json 实算：bbox 中心 (101,318)、最小外接圆心 (113,335) r=771，
 * 取 bbox 中心（罩住全部楼需要 791m，脚本里 CAMPUS_R=880 已含余量）。
 */
export const CAMPUS_CENTER = { x: 101, y: 318 }
/** 俯仰区间（度，同上语义）：4 = 近平视，78 = 近正俯视 */
export const PITCH_RANGE = [4, 78]
/** 点选建筑后飞行的时长（毫秒）与目标级别 */
export const FLY_DURATION_MS = 420
export const FLY_ZOOM = 19.0
/** 飞行后建筑在画面上的落点（容器宽高比例）：水平居中、中间偏上 ——
 *  下方让给加高后的底栏数据带，选中楼落在视觉重心上 */
export const FLY_ANCHOR = { x: 0.38, y: 0.58 }
/** 建筑名称标牌低于该缩放级别自动隐藏 */
export const LABEL_MIN_ZOOM = 16.4
/**
 * 视锥整体上移比例（占容器高度）。
 * 仰角 26° 时地平线在画面外上方，所有地面都压在下半幅 —— 实测截图里绿地的像素质心
 * 落在 y=0.70（画面中心是 0.50），看着就是"绿地没在正中"。把渲染窗口整体往下挪，
 * 等价于让画面内容上移，0.12 能把质心拉回 ~0.58。代价：顶部天际线被切掉同样一条。
 * 设 0 = 回到"注视点严格在画面正中"的几何居中。
 */
export const VIEW_SHIFT_UP = 0.12
/**
 * 标牌避让开关：23 块名称牌在全景下必然叠成一坨。开的时候按
 * 「选中 > 离相机近 > 楼号」排序，屏幕矩形相撞的名称牌收起来，
 * 只留一块楼号徽标顶上（见 loadCityTiles 的 makeBadgeTexture）——
 * 楼号永远查得到，名字只在有位置的时候出现。
 */
export const LABEL_DECLUTTER = true
/** 楼号徽标的屏显边长（像素） */
export const LABEL_BADGE_PX = 30
/** 名称牌矩形之间至少留的空白（像素），0 = 允许贴边 */
export const LABEL_COLLIDE_PAD = 6
/**
 * 路名文字之间的碰撞留白：路名是无底板文字，贴图自带一圈透明留白，
 * 再用楼名那 6px 间距会把"字根本没碰到"的两块判成重叠，直接整块藏掉。
 */
const ROAD_LABEL_COLLIDE_PAD = 0
/** 标牌屏幕固定高度（像素） */
export const LABEL_SCREEN_HEIGHT_PX = 46
/** 高度阻尼参考距离（米）：相机超过它之后标牌开始缩小 */
export const LABEL_DAMP_REF_DIST = 900

// ============ 选中态（楼体全息 + 面板详情）============
/** 选中那栋楼的不透明度：配合 depthWrite=true，读起来是"玻璃体"而不是"雾" */
export const GHOST_OPACITY = 0.42
/** 微光颜色/强度：只要一层冷蓝边调，压过 0.5 就会把立面贴图整个洗白（实测） */
export const GHOST_GLOW_COLOR = 0x4a8fe0
export const GHOST_GLOW_INTENSITY = 0.45

// ============ 后期（Bloom）============
export const BLOOM_ENABLED = true
// 曝光从 1.55 提到 1.70 的同时把辉光收小：亮度靠曝光和 IBL 给，辉光只负责"灯点发光"。
// 0.9/0.5 那版在大楼白立面上会摊成一片软糊（面积越大越明显），收到 0.7/0.35 之后
// 边缘更干净，路灯和亮窗照样有光晕。
export const BLOOM_STRENGTH = 0.7
export const BLOOM_RADIUS = 0.35
/**
 * 阈值在**线性**空间比较：composer 那条路上 three 对写进 render target 的材质一律跳过
 * 色调映射（WebGLPrograms.getParameters 里 `currentRenderTarget === null` 那个判断），
 * ACES 是最后 OutputPass 一次性做的 —— 所以 `material.toneMapped` 在这里是空转的，
 * 想控制"谁参与辉光"只能调**线性亮度本身**（发射强度、albedo、灯光强度）。
 * 被 4.0 强度暖光照到的白立面线性值本来就 >1，再叠一层 additive 就炸坑，
 * 见 environment.js 里 LIGHT_POOL_OPACITY 的注释。
 *
 * 1.0 → 1.12：夕阳光从 4.0 提到 4.6 之后，同一机位同一资产实测"过曝像素占比"从
 * 7.1% 涨到 16.7%（Temp/shot-sun40.png vs shot-trees.png 同一取样框），白立面糊成一团。
 * 抬阈值只削掉"刚好被照亮的白墙"这一档辉光，路灯 1.5 / 亮窗 1.7 仍然吃得到 Bloom。
 *
 * 1.12 → 1.30：2026-10-22 曝光 1.75 → 2.05（×1.17），阈值按同一倍率抬，保持"吃得到
 * 辉光的表面占比"不变。不抬的话近景纯白屋顶过曝到 1.52%，就是上面那句"糊成一团"。
 *
 * 1.30 → 1.55：同日曝光再提到 2.45（×1.195），阈值同倍率跟着走。
 */
export const BLOOM_THRESHOLD = 1.55

// ============ 雾（大气透视）============
export const FOG_ENABLED = true
// 雾色/清屏色走**青navy**而不是紫navy：0x0a1626 是 b-g(16) > g-r(12)，
// 远景楼块全被它染色，浅蓝幕墙再准也读成蓝紫（用户 2026-10-13 否掉"蓝紫"）。
// 2026-10-22 整体调亮：跟着曝光一起抬一档（b−g 10→14、g−r 18→26，仍是青不是紫）。
export const FOG_COLOR = NIGHT_MODE ? 0x142e3c : 0xdfe9f2
export const FOG_NEAR = 900
export const FOG_FAR = 4600

// ============ 背景城市（OSM 自建，替代原来的高德 3D 楼块）============
/** 默认是否显示周边城市体块 */
export const SHOW_CONTEXT_BUILDINGS = true

/** 场景清空色：没有底图了，画面之外就是这层夜色（和雾色一致，边界才接得上） */
const CLEAR_COLOR = NIGHT_MODE ? 0x0e222e : 0xcfd9e4

// web-mercator 赤道处每像素米数（缩放 0 级、屏幕宽 256px 的瓦片基准）
const MERCATOR_MPP = 156543.03392
/** 锚点纬度，只用于算 cos(lat) */
const ANCHOR_LAT = 30.2494594

/** zoom -> 地面每像素米数 */
export function metersPerPixel(zoom) {
  return (MERCATOR_MPP * Math.cos((ANCHOR_LAT * Math.PI) / 180)) / Math.pow(2, zoom)
}

/**
 * zoom -> 相机到目标的距离（米）。
 * 令「相机在目标平面上看到的竖直地面跨度」= 容器高度 × 每像素米数，
 * 即 2·d·tan(fov/2) = h·mpp  →  d = h·mpp / (2·tan(fov/2))。
 * 这样 DEFAULT_ZOOM 18.3 在 800px 高的容器下算出约 350m，
 * 与高德时代量到的「相机距模型中心约 400m」同一量级，观感接得上。
 */
export function zoomToDistance(zoom, viewH, fovDeg = CAMERA_FOV) {
  const h = Math.max(1, viewH)
  return (h * metersPerPixel(zoom)) / (2 * Math.tan(((fovDeg * Math.PI) / 180) / 2))
}

/** 距离 -> zoom（zoomToDistance 的逆） */
export function distanceToZoom(dist, viewH, fovDeg = CAMERA_FOV) {
  const mpp = (2 * Math.max(1, dist) * Math.tan(((fovDeg * Math.PI) / 180) / 2)) / Math.max(1, viewH)
  return Math.log2((MERCATOR_MPP * Math.cos((ANCHOR_LAT * Math.PI) / 180)) / Math.max(1e-9, mpp))
}

const _v = new THREE.Vector3()
const _v2 = new THREE.Vector3()
const _right = new THREE.Vector3()

/**
 * @param {Object} opts
 * @param {HTMLElement} opts.container
 * @param {Function} opts.loadModel 异步返回 { group, labelGroup, labelAnchors, controls }
 * @param {Function} [opts.onProgress] (loaded, total, label)
 * @param {Object} [opts.partTransforms] 分片微调 { 分片目录: { east, north, up, scale } }
 * @param {Object} [opts.buildingTransforms] 单栋楼微调 { '分片目录|楼号': {...} }
 * @param {{east?:number,north?:number}} [opts.calibration] 整体偏移（米，正=东/北）
 * @param {boolean} [opts.showContextBuildings] 是否显示周边城市
 * @param {Function} [opts.onSelect] (info|null) 选中变化
 * @param {Function} [opts.onViewChange] (info) 视角/状态变化（原 onAnchorChange）
 */
/**
 * labelGroup 里现在有两种 sprite：名称牌（'plate'，会被屏幕避让收起）和楼号徽标
 * （'badge'，名称牌收起时顶上）。凡按"一栋楼一块牌"来数的地方都只数名称牌，
 * 否则一栋楼会被当成两栋（环境层布点、微调列表、拾取候选都会翻倍）。
 */
const isPlate = (o) => o.isSprite && o.userData.labelKind === 'plate'

export async function createModelScene({
  container,
  loadModel,
  onProgress,
  partTransforms = null,
  buildingTransforms = null,
  calibration = { east: 0, north: 0 },
  showContextBuildings = SHOW_CONTEXT_BUILDINGS,
  onSelect,
  onViewChange,
}) {
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v))

  // ===== 渲染器：自建画布，直接要 WebGL2 =====
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' })
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1))
  renderer.setSize(container.clientWidth || 1, container.clientHeight || 1, false)
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = TONE_MAPPING_EXPOSURE
  renderer.setClearColor(CLEAR_COLOR, 1)
  /**
   * 真阴影：只有一盏灯投影（sceneLights 的 SHADOWS），楼是唯一的投影体。
   * ⚠️ autoUpdate 关掉 —— 场景和灯都不动，进场补渲一次就一直在。开着的代价不是"多一遍
   * 深度"这么简单：水面反射那一遍是**另一次 renderer.render(scene)**，而 shadowMap 的
   * 自动更新挂在每次 render 上，等于每帧两遍全场景深度（passes 计数见 perf()）。
   */
  renderer.shadowMap.enabled = !!SHADOWS.enabled
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  renderer.shadowMap.autoUpdate = false
  // info 默认每次 renderer.render() 自动清零，而一帧里 composer 会 render 很多次，
  // perf() 读到的永远是最后一遍（全屏四边形，1 个 draw call）—— 改成手动清零，
  // 每帧开头 reset 一次，拿到的 calls/triangles 才是"这一帧真实画了多少"。
  renderer.info.autoReset = false
  renderer.domElement.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block'
  container.appendChild(renderer.domElement)

  const scene = new THREE.Scene()
  /**
   * 调试：数一帧里"整个场景被完整渲染了几遍"。
   * Water 着色器的 onBeforeRender 会用镜像相机把 scene 再渲染一遍，水面片数一多，
   * 一帧的成本就是 N 遍全场景 —— 拖动掉帧的主因，先把它量化出来。
   */
  let scenePasses = 0
  const baseRender = renderer.render.bind(renderer)
  renderer.render = (sc, cam) => {
    if (sc === scene) scenePasses++
    return baseRender(sc, cam)
  }
  if (FOG_ENABLED) scene.fog = new THREE.Fog(FOG_COLOR, FOG_NEAR, FOG_FAR)
  // 环境贴图（IBL）：给背景幕墙/水面一条"天光"。之前全场景没有任何 envMap，纯 diffuse
  // 的夜景玻璃永远读不出反射过渡 —— 这是"没质感"的主因。
  // ⚠️ 故意**不设 scene.environment**：那会把 23 栋精模和绿地一起洗亮（精模外观不许动、
  // 绿地太亮就是"发光的睡莲叶"）。这里只把贴图挂到 environment.js 里指定的那几个材质上。
  const skyEnv = createDuskEnvironment(renderer)

  const camera = new THREE.PerspectiveCamera(
    CAMERA_FOV,
    Math.max(1, container.clientWidth) / Math.max(1, container.clientHeight),
    1,
    20000,
  )
  camera.up.set(0, 0, 1) // Z-up 世界

  const lights = createLights(scene)

  const controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.09
  controls.rotateSpeed = 0.85
  controls.zoomSpeed = 0.9
  /**
   * 左键绕心旋转 + 滚轮/中键推拉 + **右键拖拽平移**（不限距离，想拖哪儿拖哪儿）。
   * 注视点的默认落点是园区圆心 CAMPUS_CENTER（见下面"初始视角"那段），拖远了用
   * 面板上的「复位」退回默认机位。
   * screenSpacePanning=false：拖拽沿地面走（相机高度不变），符合看城市的直觉；
   * 开着的话斜着往上拖会把注视点抬到半空，整幅画面就飘了。
   */
  controls.enablePan = true
  controls.screenSpacePanning = false
  controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN }
  controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_ROTATE }
  controls.minPolarAngle = ((90 - PITCH_RANGE[1]) * Math.PI) / 180
  controls.maxPolarAngle = ((90 - PITCH_RANGE[0]) * Math.PI) / 180
  controls.minDistance = zoomToDistance(ZOOM_RANGE[1], container.clientHeight)
  controls.maxDistance = zoomToDistance(ZOOM_RANGE[0], container.clientHeight)
  controls.addEventListener('change', markDirty)

  // ===== 后期：Bloom =====
  let composer = null
  let bloomPass = null
  if (BLOOM_ENABLED) {
    const w = Math.max(1, container.clientWidth)
    const h = Math.max(1, container.clientHeight)
    composer = new EffectComposer(renderer)
    composer.setSize(w, h)
    composer.addPass(new RenderPass(scene, camera))
    bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h), BLOOM_STRENGTH, BLOOM_RADIUS, BLOOM_THRESHOLD)
    composer.addPass(bloomPass)
    composer.addPass(new OutputPass())
    // 这里不再需要动 UnrealBloomPass 的着色器：背景不透明、也不与底图做 alpha 合成，
    // 高德时代那个「alpha 被硬编码成 1 会把底图盖黑」的问题根本不存在。
  }

  // ===== 状态 =====
  let calib = { east: Number(calibration.east) || 0, north: Number(calibration.north) || 0 }
  let wrapper = null
  let loadedModel = null
  let anchorLLA = null // 模型中心的 WGS84 经纬度（纯几何算出，仅供面板显示坐标）
  let labelGroup = null
  let roadLabelGroup = null
  let roadLabelItems = []
  let envGroup = null
  let contextGroup = null
  let waterSurfaces = [] // Water 着色器对象（environment.js 里创建，渲染循环推进 time uniform）
  let waterHub = null // 水面反射调度：每次出帧前 begin()，让 4 片水面合用一次全场景反射渲染
  let ripple = null
  let selectedSprite = null
  let panelWanted = false
  let labelsWanted = true
  let contextWanted = !!showContextBuildings
  let disposed = false
  let needsRender = true
  let lastEmitKey = ''
  let rafId = 0
  let flyAnim = null
  /**
   * 飞向某栋楼"之前"的机位快照。取消选中（点别处、或再点同一栋楼）时原路退回，
   * 省得用户看完一栋楼被留在怼脸特写里找不着北。
   * 只在第一轮看楼时记一次：连着选 A→B→C 不算新起点，退回的还是这轮之前的机位。
   */
  let viewBeforeFly = null
  const viewTarget = new THREE.Vector3(0, 0, 0) // 相机注视点（模型本地世界坐标）

  function markDirty() {
    needsRender = true
  }

  // ===== 选中态：楼体透明发微光 + 面板里的详情视图 =====
  const detail = createBuildingDetailView({ renderer })
  /** @type {{meshes:THREE.Mesh[], originals:THREE.Material[]}|null} */
  let ghost = null
  /** 全息渐显的进行中状态：{ t0, dur, mats:[{mat, fromOp, fromEmi}] } */
  let ghostFade = null
  /**
   * 全息材质按"原始材质"缓存：一栋楼十几块 mesh 往往共用同一份材质，
   * 每次都 clone 会凭空造出几十个新材质、每个都要编译一遍着色器
   * （SwiftShader 上就是几百毫秒的白屏，真显卡上也是一次明显掉帧）。
   */
  const ghostCache = new WeakMap()

  /**
   * 把选中那栋楼画成"全息体"：保留它自己的贴图（外观资产不动），
   * 只把材质引用换成缓存好的全息份。
   * ⚠️ 不能直接改原材质：城市分片的材质是多栋楼共用的，一改别的楼也变透明。
   */
  function setGhost(code) {
    clearGhost()
    const controls = loadedModel && loadedModel.controls ? loadedModel.controls.buildings : null
    const meshes = controls ? controls.meshesOf(code) : null
    if (!meshes) return false
    const originals = []
    const used = []
    meshes.forEach((mesh) => {
      originals.push(mesh.material)
      // 留给 loadCityTiles 的 sourceOf：详情视图要拿原始材质，不能拿全息这份
      mesh.userData.fsgBaseMaterial = mesh.material
      if (!ghostCache.has(mesh.material)) ghostCache.set(mesh.material, makeGhost(mesh.material))
      const g = ghostCache.get(mesh.material)
      if (used.indexOf(g) < 0) used.push(g)
      mesh.material = g
    })
    ghost = { meshes, originals }
    // 全息是**渐显**的：直接换上会"啪"地变透明，和后面窗口起飞的动作接不上。
    // 时长取相机飞行同长，楼落定的那一刻正好也变成玻璃体。
    ghostFade = {
      t0: performance.now(),
      dur: FLY_DURATION_MS,
      mats: used.map((g) => ({
        mat: g,
        fromOp: g.userData.fsgFromOpacity !== undefined ? g.userData.fsgFromOpacity : 1,
        // 自发光必须从 0 起：makeGhost 已经把发光**颜色**换掉了，
        // 若从原材质的 intensity（GLTF 默认 1）起步，楼会先闪一下蓝光再暗下来
        fromEmi: 0,
      })),
    }
    return true
  }

  function stepGhost(now) {
    if (!ghostFade) return
    const p = clamp((now - ghostFade.t0) / ghostFade.dur, 0, 1)
    const k = 1 - Math.pow(1 - p, 3)
    ghostFade.mats.forEach((it) => {
      it.mat.opacity = it.fromOp + (GHOST_OPACITY - it.fromOp) * k
      if (it.mat.emissive) it.mat.emissiveIntensity = it.fromEmi + (GHOST_GLOW_INTENSITY - it.fromEmi) * k
    })
    if (p >= 1) ghostFade = null
  }

  function makeGhost(mat) {
    const g = mat.clone()
    // 记下原始不透明度与自发光：渐显从这两个值起步（各分片材质并不统一）
    g.userData.fsgFromOpacity = mat.opacity
    g.userData.fsgFromEmissive = mat.emissiveIntensity
    g.transparent = true
    g.opacity = mat.opacity
    // ⚠️ 必须继续写深度。这批城市材质本来就全是 transparent + DoubleSide
    // （外立面样式，见 facadeStyle.js），把全息份的 depthWrite 关掉会同时踩到两件事：
    //   1) 同一栋楼的正反面各叠一层，alpha 变成 1-(1-a)² —— 楼心糊成一团牛乳；
    //   2) 不写深度就没法挡住后画的其它半透明楼，透明队列又是按"质心距离"排的，
    //      于是选中的楼会被旁边楼壳盖掉一层 —— 就是用户说的"选中后像被遮挡"。
    // 写深度之后正反面由深度测试自行取舍，看起来是"干净的玻璃体"。
    g.depthWrite = true
    if (g.emissive) {
      g.emissive.setHex(GHOST_GLOW_COLOR)
      g.emissiveIntensity = 0 // 由 stepGhost 缓到 GHOST_GLOW_INTENSITY
    }
    g.needsUpdate = true
    return g
  }

  function clearGhost() {
    ghostFade = null
    if (!ghost) return
    ghost.meshes.forEach((mesh, i) => {
      mesh.material = ghost.originals[i]
      delete mesh.userData.fsgBaseMaterial
    })
    ghost = null
  }

  /**
   * 在驾驶舱面板的槽位里显示这栋楼并自转（选中态的全息材质由 selectSprite 负责）。
   * @param {string|number} code 楼号
   * @param {HTMLElement} slotEl 面板里那块透明窗口（canvas 在其下方，靠 scissor 画进去）
   */
  function showBuildingDetail(code, slotEl) {
    const src = loadedModel && loadedModel.controls ? loadedModel.controls.buildings.sourceOf(code) : null
    if (!src) return false
    detail.setSlot(slotEl || null)
    // 详情相机的方位锁成主相机此刻那一个：面板里转出来的首先是用户在地图里看到的那一面。
    // 入场已改为"直接切换"（buildingDetailView），不再传 holdMs 等待相机飞行。
    const ok = detail.show(src, {
      fromAzimuthDeg: getAzimuth(),
    })
    markDirty()
    return ok
  }

  function hideBuildingDetail() {
    // 只收三维视图，**不动全息材质**：那是"选中"的状态，面板收起≠取消选中。
    // （早先这里顺手 clearGhost，配上面板槽位先报 null 再报元素，
    //  结果每次选中都被自己抹一遍，楼永远不透明。）
    detail.hide()
    markDirty()
    return true
  }

  // ===== 视角读写（pitch / azimuth / zoom 都换算成相机球坐标）=====
  function offsetVec() {
    return _v.copy(camera.position).sub(controls.target)
  }
  function getPitch() {
    const o = offsetVec()
    const len = o.length() || 1
    return 90 - (Math.acos(clamp(o.z / len, -1, 1)) * 180) / Math.PI
  }
  function getAzimuth() {
    const o = offsetVec()
    // 0 = 正北(+Y)，顺时针为正（与高德 rotation 同向）
    return (Math.atan2(o.x, o.y) * 180) / Math.PI
  }
  function rotateAbout(axis, deltaRad) {
    const off = _v2.copy(camera.position).sub(controls.target).applyAxisAngle(axis, deltaRad)
    camera.position.copy(controls.target).add(off)
    markDirty()
  }
  function setPitch(pitchDeg) {
    const want = clamp(pitchDeg, PITCH_RANGE[0], PITCH_RANGE[1])
    const d = want - getPitch()
    if (Math.abs(d) < 1e-3) return
    _right.set(1, 0, 0)
    // 绕"过目标的水平右向量"转：相机矩阵第一列是世界右向，压掉 z 分量即水平右向
    _right.setFromMatrixColumn(camera.matrix, 0)
    _right.z = 0
    if (_right.lengthSq() < 1e-8) _right.set(1, 0, 0)
    _right.normalize()
    // ⚠️ 角度要取负：right = up × 视线偏量，按右手定则绕 +right 转是把相机**往下**压
    //（d(o)/dθ 的 z 分量 = o_z²-1 < 0）。这里不取负的话 setPitch(70) 会一路降到地平线，
    // Shift+左键调俯仰整个是反的 —— 2026-10-09 用 tools/cdp-drag.js 双向拖才量出来。
    rotateAbout(_right, (-d * Math.PI) / 180)
  }
  function setRotation(deg) {
    const d = deg - getAzimuth()
    if (Math.abs(d) < 1e-3) return
    rotateAbout(_v.set(0, 0, 1), (-d * Math.PI) / 180)
  }
  function getZoom() {
    return distanceToZoom(offsetVec().length(), container.clientHeight)
  }
  function setZoom(zoom) {
    const z = clamp(zoom, ZOOM_RANGE[0], ZOOM_RANGE[1])
    const want = zoomToDistance(z, container.clientHeight)
    const cur = offsetVec().length() || 1
    const k = want / cur
    const off = _v2.copy(camera.position).sub(controls.target).multiplyScalar(k)
    camera.position.copy(controls.target).add(off)
    markDirty()
  }

  // ===== Shift + 左键 = 俯仰（右键已经给了平移，这里给俯仰留一个不抢手势的入口）=====
  let pitchDrag = null
  const onPointerDown = (ev) => {
    if (ev.button === 0 && ev.shiftKey) {
      pitchDrag = { y: ev.clientY, pitch: getPitch() }
      controls.enabled = false
      renderer.domElement.style.cursor = 'ns-resize'
    }
    pointerDownAt = { x: ev.clientX, y: ev.clientY, t: performance.now() }
  }
  const onPointerMove = (ev) => {
    if (!pitchDrag) return
    const dy = pitchDrag.y - ev.clientY
    setPitch(pitchDrag.pitch + dy * 0.28)
  }
  const onPointerUp = () => {
    if (!pitchDrag) return
    pitchDrag = null
    controls.enabled = true
    renderer.domElement.style.cursor = ''
  }
  renderer.domElement.addEventListener('pointerdown', onPointerDown)
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)

  // ===== 标牌：屏幕恒定大小 + 高度阻尼 + 名称牌避让 =====
  const _lv = new THREE.Vector3()
  let lastLabelScale = null
  /** 每帧避让的实际判决，labelMetrics() 会带出来：查"为什么这块牌子没显示"用 */
  let lastDeclutter = { kept: [], blocked: [], offscreen: [], sel: null, pad: 0 }
  /** 路名标牌每帧的显示判决，labelMetrics() 一并带出 */
  let lastRoadLabels = { shown: [], hidden: [] }

  /** 屏幕矩形是否相交（pad = 额外留白，像素） */
  function rectsHit(a, b, pad) {
    return !(a.x1 + pad < b.x0 || b.x1 + pad < a.x0 || a.y1 + pad < b.y0 || b.y1 + pad < a.y0)
  }

  const _ax = new THREE.Vector3()
  const _ay = new THREE.Vector3()
  const _wp = new THREE.Vector3()
  const _vv = new THREE.Vector3()
  const _sr = new THREE.Vector3()
  /** sprite 四个角投影到屏幕后的包围盒（CSS 像素，y 向下），按 sprite.center 还原锚点 */
  function spriteScreenRect(sp, worldPos, vw, vh) {
    const cx = sp.center ? sp.center.x : 0.5
    const cy = sp.center ? sp.center.y : 0.5
    // Sprite 的 quad 在局部是 -0.5..+0.5，center 表示"哪个点对齐到 position"：
    // center=(0.5,0) 时整块在锚点上方，center=(0.5,0.5) 时上下各一半。
    // 之前把 cy 的方向写反了（楼名统一 center=(0.5,0)，错得一样所以没暴露），
    // 路名牌高度和楼名牌不同，混在一起做避让时就会算错一整块。
    const ex = [-cx * sp.scale.x, (1 - cx) * sp.scale.x]
    const ey = [-cy * sp.scale.y, (1 - cy) * sp.scale.y]
    let x0 = Infinity
    let x1 = -Infinity
    let y0 = Infinity
    let y1 = -Infinity
    for (const a of ex) {
      for (const b of ey) {
        _sr.copy(worldPos).addScaledVector(_ax, a).addScaledVector(_ay, b).project(camera)
        const px = (_sr.x * 0.5 + 0.5) * vw
        const py = (-_sr.y * 0.5 + 0.5) * vh
        if (px < x0) x0 = px
        if (px > x1) x1 = px
        if (py < y0) y0 = py
        if (py > y1) y1 = py
      }
    }
    return { x0, x1, y0, y1 }
  }

  function updateLabelScales() {
    if (!labelGroup && !roadLabelGroup) return
    // ⚠️ 分母必须是**容器 CSS 高度**，不是 renderer.domElement.height：
    // 后者含 devicePixelRatio，拿它算牌子会随 dpr 放大一倍。
    const vw = Math.max(1, container.clientWidth)
    const vh = Math.max(1, container.clientHeight)
    const camDist = offsetVec().length()
    const damp = Math.max(0.62, Math.min(1, LABEL_DAMP_REF_DIST / Math.max(1, camDist)))
    const platePx = LABEL_SCREEN_HEIGHT_PX * damp
    const k = (2 * Math.tan((camera.fov * Math.PI) / 360)) / vh
    lastLabelScale = { vh, camDist: Math.round(camDist), damp: +damp.toFixed(3), px: +platePx.toFixed(2) }
    // ⚠️ 牌子世界位置要经过 cityWrapper（rotX 90° + 校准偏移）才成立，父链没刷过就是
    // 单位矩阵，23 块牌子会全按错位算尺寸（实测 21 号楼 72px、1 号楼 31px，本该 38px）。
    // 但也不能整棵 scene 刷：那棵树有上万节点，渲染器自己每帧已经刷过一遍了。
    // updateWorldMatrix(向上, 向下) 正好只覆盖"父链 + 标牌"这一小撮。
    if (labelGroup) labelGroup.updateWorldMatrix(true, true)
    camera.updateMatrixWorld()
    // ⚠️ matrixWorldInverse 同样只有 renderer.render() 内部会刷新，这里得自己算，
    // 否则第一帧拿到的是单位矩阵，视深=世界 z，尺寸和避让矩形全错。
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert()
    _ax.setFromMatrixColumn(camera.matrixWorld, 0).normalize()
    _ay.setFromMatrixColumn(camera.matrixWorld, 1).normalize()
    if (!labelGroup) {
      layoutRoadLabels(vw, vh, k, damp)
      return
    }

    // 1) 逐块牌子：世界尺寸按"屏显恒定"反算，同时记下它在屏幕上的矩形
    const items = []
    labelGroup.children.forEach((o) => {
      if (!o.isSprite) return
      const u = o.userData
      const isPlate = u.labelKind !== 'badge'
      const hpx = isPlate ? platePx : LABEL_BADGE_PX
      _lv.setFromMatrixPosition(o.matrixWorld)
      const dist = _lv.distanceTo(camera.position)
      // ⚠️ 先回到相机空间拿**视深**，屏显尺寸只能按视深反算：
      // 用欧氏距离 dist 的话，偏离画面中心的牌子会按 1/cos(θ) 一起放大
      // （实测同屏最高的 21 号楼 74px、边缘的 1 号楼 32px，本该都是 38px），
      // 放大后的块子又把避让挤掉一大片名称牌。
      _vv.copy(_lv).applyMatrix4(camera.matrixWorldInverse)
      const depth = Math.max(1, -_vv.z)
      const h = hpx * k * depth
      o.scale.set(h * (u.aspect || 1), h, 1)
      const leader = u.leader
      if (leader) {
        const lw = LEADER_LINE_STYLE.widthPx * k * depth
        leader.position.copy(u.cityPos)
        leader.scale.set(lw, Math.max(0, u.cityPos.y - u.ground.y), lw)
      }
      _wp.copy(_lv)
      _sv.copy(_lv).project(camera)
      // ⚠️ 不能只看 project() 后的 z：点在相机**背后**时 w<0，除法把 x/y 翻个符号、
      // z 也可能落进 [-1,1]，于是"看不见的牌子"被判成在画面里，四角一投影就炸成
      // 盖住半屏的白块（实测 8 号楼那块就是）。要回相机空间用视深判。
      const ndcX = (_sv.x * 0.5 + 0.5) * vw
      const ndcY = (-_sv.y * 0.5 + 0.5) * vh
      items.push({
        o,
        u,
        isPlate,
        dist,
        code: u.buildingCode,
        anchor: [Math.round(ndcX), Math.round(ndcY)],
        onScreen: _vv.z < -camera.near && _vv.z > -camera.far && ndcX > -60 && ndcX < vw + 60 && ndcY > -60 && ndcY < vh + 60,
        rect: spriteScreenRect(o, _wp, vw, vh),
      })
    })

    // 2) 名称牌避让：选中的一定留，其余按"离相机近优先、再按楼号"抢位置
    const selCode = selectedSprite ? selectedSprite.userData.buildingCode : null
    const plates = items
      .filter((it) => it.isPlate)
      .sort(
        (a, b) =>
          (a.code === selCode ? 0 : 1) - (b.code === selCode ? 0 : 1) ||
          a.dist - b.dist ||
          String(a.code).localeCompare(String(b.code), 'zh'),
      )
    const taken = []
    const shownPlate = new Set()
    lastDeclutter = { kept: [], blocked: [], offscreen: [], sel: selCode, pad: LABEL_COLLIDE_PAD }
    plates.forEach((it) => {
      if (!it.onScreen) {
        lastDeclutter.offscreen.push(it.code)
        return // 相机背后/画面外的：第 3 步统一收起，不参与抢位
      }
      const forced = it.code === selCode
      const blocked = !forced && LABEL_DECLUTTER && taken.some((r) => rectsHit(r, it.rect, LABEL_COLLIDE_PAD))
      if (blocked) {
        lastDeclutter.blocked.push(it.code)
        return
      }
      taken.push(it.rect)
      shownPlate.add(it)
      lastDeclutter.kept.push(it.code)
    })

    // 3) 落可见性：名称牌露出的，徽标收起；被避让掉的，露徽标顶上；画面外的两者都收
    items.forEach((it) => {
      if (!it.isPlate) return
      const on = it.onScreen && (!LABEL_DECLUTTER || shownPlate.has(it) || it.code === selCode)
      it.o.visible = on
      if (it.u.leader) it.u.leader.visible = on
      if (it.u.badge) it.u.badge.visible = it.onScreen && !on
    })
    layoutRoadLabels(vw, vh, k, damp)
  }

  /**
   * 路名标牌：每个字单独一个 Sprite，字永远正立，整串字沿道路的屏幕投影方向排开。
   *
   * 为什么不用"整块贴图 + SpriteMaterial.rotation"：
   *   · 用户要的是高德那种"单字跟着路走、字本身不倒"的观感；
   *   · 整块旋转会把"秋涛北路"斜成一整条，竖街尤其别扭。
   *
   * 排法：
   *   1. 把道路切向（bearing 0=北、顺时针）在地面上取 30m 外的点，投到屏幕得到方向；
   *   2. 横街取左→右、竖街取上→下，保证正常阅读顺序；
   *   3. 每个字按屏幕间距排在方向线上，再反投影回同一视深平面 —— 这样字链的
   *      屏幕走向严格等于道路走向，但每个字仍是独立 billboard，永远正立。
   *   4. 整条字链的屏幕矩形参与路名之间的避让；与楼名不做互斥（楼名压在上层）。
   */
  function layoutRoadLabels(vw, vh, k, damp) {
    if (!roadLabelGroup) return
    roadLabelGroup.updateWorldMatrix(true, true)
    const charPx = ROAD_LABEL_SCREEN_HEIGHT_PX * damp
    const taken = []
    const items = []
    lastRoadLabels = { shown: [], hidden: [], blocked: [] }

    roadLabelItems.forEach((it) => {
      _lv.copy(it.anchor)
      const dist = _lv.distanceTo(camera.position)
      _vv.copy(_lv).applyMatrix4(camera.matrixWorldInverse)
      const depth = Math.max(1, -_vv.z)
      const h = charPx * k * depth
      _sv.copy(_lv).project(camera)
      const sx = (_sv.x * 0.5 + 0.5) * vw
      const sy = (-_sv.y * 0.5 + 0.5) * vh
      const ndcZ = _sv.z
      const onScreen =
        _vv.z < -camera.near &&
        _vv.z > -camera.far &&
        sx > -220 && sx < vw + 220 && sy > -160 && sy < vh + 160
      if (!onScreen) {
        it.visible = false
        it.onScreen = false
        it.screenRect = null
        it.chars.forEach((c) => (c.sprite.visible = false))
        lastRoadLabels.hidden.push(it.name)
        return
      }

      // 道路在屏幕上的方向（取切向 30m 外的点投影）
      const bearing = ((it.bearing || 0) * Math.PI) / 180
      _wp.set(_lv.x + Math.sin(bearing) * 30, _lv.y + Math.cos(bearing) * 30, _lv.z)
      _sv.copy(_wp).project(camera)
      let dx = (_sv.x * 0.5 + 0.5) * vw - sx
      let dy = (-_sv.y * 0.5 + 0.5) * vh - sy
      const len = Math.hypot(dx, dy)
      if (len < 1e-3) {
        dx = 1
        dy = 0
      } else {
        dx /= len
        dy /= len
      }
      // 横街从左到右读，竖街从上到下读
      if (Math.abs(dx) >= Math.abs(dy)) {
        if (dx < 0) {
          dx = -dx
          dy = -dy
        }
      } else if (dy < 0) {
        dx = -dx
        dy = -dy
      }

      // 斜排时按主轴分量放大间距，避免两个正立字的包围盒压在一起。
      // ⚠️ 这里算的是**屏幕像素**间距，用 charPx，不能用世界高度 h。
      const axis = Math.max(Math.abs(dx), Math.abs(dy), 0.01)
      const step = (charPx * ROAD_LABEL_CHAR_STEP) / axis
      const start = -((it.chars.length - 1) * step) / 2
      let minX = Infinity
      let maxX = -Infinity
      let minY = Infinity
      let maxY = -Infinity

      it.chars.forEach((c, i) => {
        const off = start + i * step
        const cx = sx + dx * off
        const cy = sy + dy * off
        const wWorld = h * c.aspect
        const wPx = charPx * c.aspect
        // 反投影到同一视深平面：字心正好落在屏幕上的 (cx, cy)
        _wp.set((cx / vw) * 2 - 1, -(cy / vh) * 2 + 1, ndcZ)
        _wp.unproject(camera)
        c.sprite.position.copy(_wp)
        c.sprite.scale.set(wWorld, h, 1)
        c.sprite.visible = true
        if (cx - wPx / 2 < minX) minX = cx - wPx / 2
        if (cx + wPx / 2 > maxX) maxX = cx + wPx / 2
        if (cy - charPx / 2 < minY) minY = cy - charPx / 2
        if (cy + charPx / 2 > maxY) maxY = cy + charPx / 2
      })

      it.visible = true
      it.onScreen = true
      it.screenX = sx
      it.screenY = sy
      it.screenRect = { x0: minX, x1: maxX, y0: minY, y1: maxY }
      items.push({ it, dist, rect: it.screenRect })
    })

    items.sort((a, b) => a.dist - b.dist)
    items.forEach(({ it, rect }) => {
      const hit = taken.find((t) => rectsHit(t.rect, rect, ROAD_LABEL_COLLIDE_PAD))
      it.visible = !hit
      it.chars.forEach((c) => (c.sprite.visible = !hit))
      if (hit) {
        lastRoadLabels.hidden.push(it.name)
        lastRoadLabels.blocked.push({ name: it.name, by: hit.name })
      } else {
        taken.push({ rect, name: it.name })
        lastRoadLabels.shown.push(it.name)
      }
    })
  }

  function updateLabelVisibility() {
    const z = getZoom()
    if (labelGroup) {
      labelGroup.visible = labelsWanted && z >= LABEL_MIN_ZOOM
      if (!labelGroup.visible && selectedSprite) selectSprite(null)
    }
    if (roadLabelGroup) roadLabelGroup.visible = labelsWanted && z >= ROAD_LABEL_MIN_ZOOM
    markDirty()
  }

  // ===== 选中与拾取 =====
  const raycaster = new THREE.Raycaster()
  const ndc = new THREE.Vector2()
  const _sv = new THREE.Vector3()
  let pointerDownAt = null

  /**
   * 选中标牌的屏幕位置，每次真正出帧时刷新（emitSelection 里顺带算好，不额外投影一遍）。
   * 给"贴着标牌飘"的信息弹窗用：它自带 rAF 读这一个对象直接写 style.left/top，
   * 不走 onSelect 那条 Vue 投递 —— 原因见下面"键里不能放屏幕坐标"那段注释。
   */
  const selScreen = { code: '', x: 0, y: 0, labelRight: 0, visible: false }

  function emitSelection(force) {
    if (!onSelect) return
    if (!selectedSprite) {
      selScreen.code = ''
      selScreen.visible = false
      if (force || lastEmitKey !== 'none') {
        lastEmitKey = 'none'
        onSelect(null)
      }
      return
    }
    selectedSprite.updateWorldMatrix(true, false)
    _sv.setFromMatrixPosition(selectedSprite.matrixWorld).project(camera)
    const w = container.clientWidth
    const h = container.clientHeight
    const x = (_sv.x * 0.5 + 0.5) * w
    const y = (-_sv.y * 0.5 + 0.5) * h
    const damp = lastLabelScale ? lastLabelScale.damp : 1
    const labelRight = x + (LABEL_SCREEN_HEIGHT_PX * damp * (selectedSprite.userData.aspect || 4)) / 2
    const u = selectedSprite.userData
    const gv = u.ground.clone().applyMatrix4(selectedSprite.parent.matrixWorld).project(camera)
    const gx = (gv.x * 0.5 + 0.5) * w
    const gy = (-gv.y * 0.5 + 0.5) * h
    // 每帧刷新的实时位置（供贴牌弹窗轮询），与下面的去重投递互不相干
    selScreen.code = u.buildingCode
    selScreen.x = x
    selScreen.y = y
    selScreen.labelRight = labelRight
    selScreen.visible = _sv.z <= 1
    // ⚠️ 键里不能放屏幕坐标：相机飞行与阻尼期间每帧都在变，等于每帧发给 Vue 一个新的
    // selection 对象，整块驾驶舱（列表 + 图表 + 三维槽）跟着重渲染 —— 这就是"点一下
    // 建筑很卡"的一半成因。面板早已不跟着牌子飘了，只有"选中了谁 / 可见性变了"需要通知。
    const key = `${u.buildingCode}:${_sv.z <= 1}:${panelWanted}`
    if (!force && key === lastEmitKey) return
    lastEmitKey = key
    onSelect({
      code: u.buildingCode,
      name: u.buildingName,
      part: u.part,
      lla: u.lla,
      sphereRadius: u.sphereRadius,
      footprintRadius: u.footprintRadius || null, // 实测占地（无实测为 null，面板据此标"估算"）
      screen: { x, y },
      labelRight,
      groundScreen: { x: gx, y: gy },
      visible: _sv.z <= 1,
      panel: panelWanted,
    })
  }

  /**
   * 让某栋楼落在画面 FLY_ANCHOR 处：直接解出注视点，不需要旧实现那种
   * "飞完再量残差、迭代校正三轮"的补丁 —— 那套复杂度全是高德异步 center 造成的，
   * 自己持有相机之后是一步精确解。
   */
  function targetToPutPointAt(world, fx, fy, dist) {
    const w = Math.max(1, container.clientWidth)
    const h = Math.max(1, container.clientHeight)
    const upp = (2 * dist * Math.tan((camera.fov * Math.PI) / 360)) / h
    const dx = (fx - 0.5) * w
    // 视锥整体上移后，"落在屏幕第 r 行"对应的是未偏移前的第 r + offsetY 行。
    // 直接读 camera.view 里真正生效的偏移量，避免和 syncSize 各算一份比例。
    const vy = camera.view && camera.view.enabled ? camera.view.offsetY : 0
    const dy = (fy - 0.5) * h + vy
    _right.setFromMatrixColumn(camera.matrix, 0).normalize() // 屏幕右向
    const up = _v2.setFromMatrixColumn(camera.matrix, 1).normalize() // 屏幕上向
    // P = T + dx·upp·right − dy·upp·up  →  T = P − dx·upp·right + dy·upp·up
    return world.clone().addScaledVector(_right, -dx * upp).addScaledVector(up, dy * upp)
  }

  function flyTo(sprite, zoom, anchorPt) {
    if (!sprite) return
    const g = sprite.userData.ground.clone().applyMatrix4(sprite.parent.matrixWorld)
    const dist = zoomToDistance(zoom, container.clientHeight)
    const target = anchorPt ? targetToPutPointAt(g, anchorPt.x, anchorPt.y, dist) : g.clone()
    // 保持当前朝向与俯仰，只换注视点与距离
    const dir = offsetVec().normalize()
    if (!viewBeforeFly) viewBeforeFly = { pos: camera.position.clone(), target: controls.target.clone() }
    flyAnim = {
      t0: performance.now(),
      dur: FLY_DURATION_MS,
      fromPos: camera.position.clone(),
      fromTarget: controls.target.clone(),
      toPos: target.clone().addScaledVector(dir, dist),
      toTarget: target,
    }
    markDirty()
  }

  function selectSprite(sprite, opts) {
    // 点中的如果是楼号徽标，改选它对应的名称牌：徽标只是名称牌被避让掉时的替身，
    // 光圈半径、飞行锚点、面板要的 ground / sphereRadius 这些字段全在名称牌上。
    if (sprite && sprite.userData.labelKind === 'badge') sprite = sprite.userData.plate || sprite
    const o = opts || {}
    selectedSprite = sprite
    panelWanted = !!o.panel
    markDirty()
    // 全息材质跟着"选中"走，不跟面板走：面板收起时楼仍然是被选中的状态
    setGhost(sprite ? sprite.userData.buildingCode : null)
    if (ripple && sprite) {
      const g = sprite.userData.ground
      ripple.select({ x: g.x, y: g.y, z: g.z, radius: rippleRadiusForBuilding(sprite.userData) })
      if (o.fly !== false) flyTo(sprite, Math.max(getZoom(), FLY_ZOOM), FLY_ANCHOR)
    } else if (ripple) {
      ripple.clear()
      // 取消选中 = 详情视图和全息材质都要跟着退场，否则楼会一直半透明、
      // 面板里还转着一栋已经没人选中的楼
      if (!sprite) hideBuildingDetail()
    }
    emitSelection(true)
  }

  function clearSelection() {
    panelWanted = false
    selectSprite(null)
    // 弹窗因为"点了别处/再点同一栋"而收起 → 相机退回看这轮楼之前的机位
    const s = viewBeforeFly
    viewBeforeFly = null
    if (!s) return
    flyAnim = {
      t0: performance.now(),
      dur: FLY_DURATION_MS,
      fromPos: camera.position.clone(),
      fromTarget: controls.target.clone(),
      toPos: s.pos,
      toTarget: s.target,
    }
    markDirty()
  }

  /** 详情面板开关（点名称牌时由组件调用）。必须是有名字的函数：onClick 里要引用它 */
  function setPanelOpen(open) {
    if (!selectedSprite) return false
    panelWanted = !!open
    emitSelection(true)
    return true
  }

  function spriteOfBuilding(part, code) {
    if (!labelGroup) return null
    const sprites = labelGroup.children.filter(isPlate)
    return (
      sprites.find((o) => o.userData.part === part && o.userData.buildingCode === code) ||
      sprites.find((o) => o.userData.buildingCode === code) ||
      null
    )
  }

  function onClick(ev) {
    if (!camera || !labelGroup) return
    const start = pointerDownAt
    pointerDownAt = null
    if (!start) return
    if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) > 6) return // 拖动不算点击
    if (performance.now() - start.t > 700) return // 长按不算点击
    const rect = container.getBoundingClientRect()
    ndc.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1
    ndc.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1
    raycaster.setFromCamera(ndc, camera)

    let labelHit = null
    if (labelGroup.visible) {
      // ⚠️ 必须自己滤 visible：three 的 Raycaster 不看 object.visible
      //（见 node_modules/three/src/core/Raycaster.js 的 intersectObject），
      // 被避让收起的名称牌仍然会被射线打到，不滤就会"点空白处选中一块看不见的牌"。
      const hits = raycaster.intersectObjects(labelGroup.children, false).filter((h) => h.object.visible)
      if (hits.length) labelHit = hits[0].object
    }
    if (labelHit) {
      if (labelHit === selectedSprite) setPanelOpen(!panelWanted)
      else selectSprite(labelHit, { panel: true })
      return
    }
    const pick = loadedModel && loadedModel.controls ? loadedModel.controls.buildings.pickMeshes() : []
    if (pick.length) {
      const bh = raycaster.intersectObjects(pick, false)
      if (bh.length) {
        const ud = bh[0].object.userData
        const sprite = spriteOfBuilding(ud.part, ud.buildingCode)
        if (sprite) {
          if (sprite === selectedSprite) clearSelection()
          else selectSprite(sprite, { panel: true })
          return
        }
      }
    }
    clearSelection()
  }
  renderer.domElement.addEventListener('click', onClick)

  // ===== 尺寸变化 =====
  function syncSize() {
    const w = Math.max(1, container.clientWidth)
    const h = Math.max(1, container.clientHeight)
    if (lastW === w && lastH === h) return
    lastW = w
    lastH = h
    camera.aspect = w / h
    camera.updateProjectionMatrix()
    // 视锥整体上移：偏移量是像素，所以每次尺寸变化都要按新高度重算比例
    const shift = clamp(VIEW_SHIFT_UP, -0.4, 0.4) * h
    if (Math.abs(shift) > 0.5) camera.setViewOffset(w, h, 0, shift, w, h)
    else camera.clearViewOffset()
    renderer.setSize(w, h, false)
    if (composer) composer.setSize(w, h)
    if (bloomPass) bloomPass.setSize(w, h)
    markDirty()
  }
  let lastW = 0
  let lastH = 0
  window.addEventListener('resize', syncSize)
  syncSize()

  // ===== 模型加载 =====
  const city = await Promise.resolve().then(loadModel)
  if (disposed) return null

  wrapper = new THREE.Group()
  wrapper.name = 'cityWrapper'
  wrapper.rotation.x = Math.PI / 2 // Y-up -> Z-up
  wrapper.add(city.group)
  scene.add(wrapper)
  loadedModel = city
  anchorLLA = city.centerLLA || null
  labelGroup = city.labelGroup || null

  // 整体校准偏移：直接挪 world，模型/标牌/光圈/环境层一起跟
  wrapper.position.set(calib.east, calib.north, 0)

  // 分片 / 单栋楼微调
  if (city.controls) {
    const dirs = partTransforms ? Object.keys(partTransforms) : []
    dirs.forEach((d) => city.controls.parts.set(d, partTransforms[d]))
    const bKeys = buildingTransforms ? Object.keys(buildingTransforms) : []
    bKeys.forEach((k) => {
      const hit = city.controls.buildings.list.find((b) => b.key === k || b.code === String(k))
      if (hit) city.controls.buildings.set(hit.key, buildingTransforms[k])
    })
    if (dirs.length || bKeys.length) console.info(`[sceneView] 已应用微调：分片 ${dirs.length} 个、单栋楼 ${bKeys.length} 栋`)
  }

  ripple = createBuildingRipple()
  city.group.add(ripple.group)

  // 路名标牌：世界系坐标（东, 北, 上），直接挂 scene，不跟随 cityWrapper 的分片微调
  const roads = createRoadLabels()
  roadLabelGroup = roads.group
  roadLabelItems = roads.items
  scene.add(roadLabelGroup)

  // 环境层（草地/树/道路/水体/周边城市）
  const envBuildings = []
  if (city.labelGroup) {
    city.labelGroup.children.forEach((o) => {
      if (!isPlate(o)) return
      const g = o.userData.ground
      envBuildings.push({
        east: g.x,
        north: -g.z,
        radius: o.userData.footprintRadius || (o.userData.sphereRadius || 80) * 0.62,
        measured: !!o.userData.footprintRadius,
        name: o.userData.buildingName,
      })
    })
  }
  // 树位清单的"躲楼"是在 Blender 里按 art/building-layout.json 的圆做的，而那份布局是
  // **模型校准之前**导出的。拿真正渲染出来的楼盒一比：平均偏西 162m，而且 21 个分片各偏
  // 各的（dx 离散度 181m，不是刚性偏移）—— 清单里"已经躲开楼"的树，实测有 1091 棵树干
  // 正好落在楼体 footprint 里（13 号楼 296 棵、20 号楼 207 棵、8 号楼 173 棵）。
  // 所以禁区不能用那套圆，要用渲染结果本身：校准怎么改，禁区跟着改。
  function renderedBuildingBlockers() {
    const buildings = loadedModel && loadedModel.controls ? loadedModel.controls.buildings : null
    if (!buildings || !city.labelGroup) return []
    city.group.updateMatrixWorld(true)
    const out = []
    const seen = new Set()
    city.labelGroup.children.forEach((o) => {
      const code = o.userData && o.userData.buildingCode
      if (!code || seen.has(code)) return
      seen.add(code)
      const src = buildings.sourceOf(code)
      if (!src || !src.box || src.box.isEmpty()) return
      out.push([src.box.min.x, src.box.max.x, src.box.min.y, src.box.max.y])
    })
    return out
  }

  /**
   * 每栋编号楼的真实占地矩形（带楼号/楼名），是 renderedBuildingBlockers 的"带标识"版本。
   * 离线资产生成器（art/build_context.py 的"重合"判据）应当用它做**矩形 vs 多边形**的
   * 精确相交测试，而不是拿布局圆去近似 —— 圆对长方形楼必然多盖一圈，靠圆判"重合"
   * 要么漏（部分重叠不算）要么误杀（只是靠近的被算成重合）。
   */
  function renderedBuildingBoxesByCode() {
    const buildings = loadedModel && loadedModel.controls ? loadedModel.controls.buildings : null
    if (!buildings || !city.labelGroup) return []
    city.group.updateMatrixWorld(true)
    const out = []
    const seen = new Set()
    city.labelGroup.children.forEach((o) => {
      const code = o.userData && o.userData.buildingCode
      if (!code || seen.has(code)) return
      seen.add(code)
      const src = buildings.sourceOf(code)
      if (!src || !src.box || src.box.isEmpty()) return
      out.push({
        code: String(code),
        name: (o.userData && o.userData.buildingName) || null,
        minE: src.box.min.x, maxE: src.box.max.x,
        minN: src.box.min.y, maxN: src.box.max.y,
        minZ: src.box.min.z, maxZ: src.box.max.z,
      })
    })
    return out
  }

  envGroup = await createEnvironment({
    buildings: envBuildings,
    blockers: renderedBuildingBlockers(),
    envTexture: skyEnv ? skyEnv.texture : null,
  })
  if (envGroup) {
    envGroup.userData.envBuildings = envBuildings
    scene.add(envGroup)
    // 周边城市（OSM 背景楼 + 城市地面 + 城市街网）单独抓出来，供显隐开关用
    envGroup.children.forEach((c) => {
      if (c.name === 'context') contextGroup = c
    })
    if (contextGroup) contextGroup.visible = contextWanted
    // Water 着色器水面：收集 Water 对象，渲染循环里推进 time uniform 出涟漪动画
    waterSurfaces = (envGroup.userData.waterSurfaces || []).filter((w) => w && w.material && w.material.uniforms && w.material.uniforms.time)
    waterHub = envGroup.userData.waterHub || null
    // 标牌（46 块名称牌 + 徽标 + 引线）在 256² 的反射贴图里根本占不到像素，别跟着画
    if (waterHub && labelGroup) waterHub.reflectionSkip.push(labelGroup)
    if (waterHub && roadLabelGroup) waterHub.reflectionSkip.push(roadLabelGroup)
    const st = envGroup.userData
    console.info(
      `[sceneView] 环境层已叠加（${st.source}）：水面 ${st.hasWater ? 1 : 0}、道路 ${st.hasRoads ? 1 : 0}、` +
        `周边城市 ${st.hasContext ? 1 : 0}、Water着色器 ${waterSurfaces.length} 片`,
    )
  }

  // ===== 真阴影：把投影灯的正交视锥贴到编号楼的包围盒上 =====
  /** 上一次贴合的结果（{side, texel}），调试口 getShadowFit 直接吐它 */
  let shadowFit = null
  function fitShadows() {
    const caster = lights.find((l) => l.isDirectionalLight && l.castShadow)
    if (!caster || !wrapper || !city || !city.box) return null
    // 还没渲染过，matrixWorld 是旧的：先按"祖先 + 自己"算一次（子树留给渲染时再算）
    wrapper.updateWorldMatrix(true, false)
    // city.box 是分片自己的 Y-up 局部盒，套上 wrapper（rotX90 + 校准偏移）才是世界盒
    const box = city.box.clone().applyMatrix4(wrapper.matrixWorld)
    shadowFit = fitShadowCamera(caster, box)
    renderer.shadowMap.needsUpdate = true // autoUpdate 是关着的，靠这一次把图烘出来
    markDirty()
    console.info(
      `[sceneView] 阴影相机贴合：盒边长 ${shadowFit.side.toFixed(0)}m、` +
        `texel ${shadowFit.texel.toFixed(2)}m、map ${SHADOWS.mapSize}、灯 ${caster.name}`,
    )
    return shadowFit
  }
  fitShadows()

  // 初始/复位注视点 = 园区圆心 CAMPUS_CENTER，**不是** 23 栋楼锚点的平均。
  // 之前用平均：楼群分布是歪的（23 栋里 10 栋在西南、2 栋在东北），
  // 平均中心落在 (-28, 67)，而绿地/路网/地台渐隐全按 bbox 圆心 (101, 318) 铺
  // —— 两者差 282m，默认机位下相当于画面横移三百多像素，看着就是"绿地不在正中"。
  viewTarget.set(CAMPUS_CENTER.x + calib.east, CAMPUS_CENTER.y + calib.north, 0)
  const d0 = zoomToDistance(DEFAULT_ZOOM, container.clientHeight)
  const a0 = (DEFAULT_AZIMUTH * Math.PI) / 180
  const p0 = (DEFAULT_PITCH * Math.PI) / 180
  controls.target.copy(viewTarget)
  camera.position.set(
    viewTarget.x + d0 * Math.cos(p0) * Math.sin(a0),
    viewTarget.y + d0 * Math.cos(p0) * Math.cos(a0),
    viewTarget.z + d0 * Math.sin(p0),
  )
  camera.lookAt(controls.target)
  controls.update()

  updateLabelVisibility()
  emitSelection(true)
  markDirty()
  if (onProgress) onProgress(1, 1, '模型加载完成')
  console.info(`[sceneView] 场景就绪：标牌 ${city.labelAnchors ? city.labelAnchors.length : 0} 块，zoom ${getZoom().toFixed(2)}，俯仰 ${getPitch().toFixed(0)}°`)
  emitView()

  // ===== 渲染循环（自己持有 rAF，不再靠高德调度）=====
  let renderCount = 0
  let lastDetailFrame = 0 // 只为面板自转而画的帧，用来限帧
  let lastCost = 0
  let avgCost = 0
  /** 最近一个真正出帧的耗时分解 + 绘制统计（拖动卡顿时用它定位是哪一段） */
  let lastStages = null
  let wasMoving = false // 用于抓"惯性收敛的那一帧"
  const loop = () => {
    if (disposed) return
    rafId = requestAnimationFrame(loop)
    const nowMs = performance.now()
    if (flyAnim) {
      const t = clamp((nowMs - flyAnim.t0) / flyAnim.dur, 0, 1)
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2 // easeInOutQuad
      camera.position.lerpVectors(flyAnim.fromPos, flyAnim.toPos, e)
      controls.target.lerpVectors(flyAnim.fromTarget, flyAnim.toTarget, e)
      if (t >= 1) flyAnim = null
      needsRender = true
    }
    const moving = controls.update() // 有阻尼/交互时返回 true
    // 惯性收敛的那一帧补画一次：水面反射在拖动途中不出图，就是等这一帧补上
    if (!moving && wasMoving) needsRender = true
    wasMoving = moving
    // 详情视图在自转，只要它开着就必须一直出帧（scissor 那一遍也挂在这里）
    const detailOn = detail.active
    const busy = needsRender || moving || flyAnim || (ripple && ripple.active) || detail.interacting || !!ghostFade
    if (!busy && !detailOn) return
    // 纯粹"为了自转"而画的帧压到 ~30fps：整场景 + Bloom 走一遍并不便宜，而慢速旋转
    // 30fps 和 60fps 肉眼分不出来。用户一动（busy 为真）立刻回到全速。
    if (!busy && nowMs - lastDetailFrame < 30) return
    lastDetailFrame = nowMs
    needsRender = !!moving || !!flyAnim
    const t0 = performance.now()
    renderer.info.reset()
    scenePasses = 0
    stepGhost(t0)
    updateLabelScales()
    if (ripple && selectedSprite) {
      const g = selectedSprite.userData.ground
      ripple.moveTo(g.x, g.y, g.z, rippleRadiusForBuilding(selectedSprite.userData))
    }
    const frameDt = Math.min(0.1, (t0 - lastFrameAt) / 1000)
    if (ripple) ripple.update(frameDt)
    lastFrameAt = t0
    // Water 着色器涟漪：推进 time uniform（仅在场景已渲染时推进，按需渲染不额外拉帧）
    if (waterSurfaces.length) {
      for (let i = 0; i < waterSurfaces.length; i++) {
        waterSurfaces[i].material.uniforms.time.value += frameDt
      }
    }
    const tLabels = performance.now()
    if (waterHub) waterHub.begin(moving) // 拖动途中跳过水面反射那一遍（≈20ms/帧）
    if (composer) composer.render()
    else renderer.render(scene, camera)
    const tDraw = performance.now()
    if (detailOn) detail.render()
    lastCost = performance.now() - t0
    lastStages = {
      labels: +(tLabels - t0).toFixed(2),
      draw: +(tDraw - tLabels).toFixed(2),
      detail: +(performance.now() - tDraw).toFixed(2),
      calls: renderer.info.render.calls,
      triangles: renderer.info.render.triangles,
      programs: renderer.info.programs.length,
      passes: scenePasses,
      waters: waterSurfaces.length,
    }
    avgCost = avgCost ? avgCost * 0.9 + lastCost * 0.1 : lastCost
    renderCount++
    emitSelection(false)
  }
  let lastFrameAt = performance.now()
  loop()

  function emitView() {
    if (!onViewChange) return
    // wgs84 / gcj 仍然要给：校准面板那两行坐标就靠它。
    // 来源是 loadCityTiles 由 tileset 变换算出的模型中心经纬度（纯数学，与高德无关）。
    const lla = anchorLLA || { lat: 0, lon: 0 }
    onViewChange({
      wgs84: { lat: lla.lat, lon: lla.lon },
      gcj: wgs2gcj(lla.lon, lla.lat),
      zoom: +getZoom().toFixed(3),
      pitch: +getPitch().toFixed(1),
      rotation: +getAzimuth().toFixed(1),
      calib: { ...calib },
      precise: !!loadedModel,
      context: contextWanted,
      buildings: contextWanted,
      labels: labelsWanted,
    })
  }
  controls.addEventListener('change', emitView)

  // ===== 控制器 API =====
  function setCalibration(next) {
    calib = { east: Number(next && next.east) || 0, north: Number(next && next.north) || 0 }
    if (wrapper) wrapper.position.set(calib.east, calib.north, 0)
    fitShadows() // 楼挪了，阴影视锥得跟着挪，否则校准一拉影子就跑出盒子
    markDirty()
    emitView()
    return { ...calib }
  }

  function setContextVisible(show) {
    contextWanted = !!show
    if (contextGroup) contextGroup.visible = contextWanted
    markDirty()
    emitView()
    return contextWanted
  }

  function dispose() {
    disposed = true
    cancelAnimationFrame(rafId)
    renderer.domElement.removeEventListener('pointerdown', onPointerDown)
    renderer.domElement.removeEventListener('click', onClick)
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    window.removeEventListener('resize', syncSize)
    controls.dispose()
    if (ripple) ripple.dispose()
    if (waterHub) waterHub.dispose()
    clearGhost()
    detail.dispose() // 只放它自己的克隆材质和展台几何，楼体 geometry 与主场景共享不能碰
    scene.traverse((o) => {
      if (o.geometry) o.geometry.dispose()
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material]
        mats.forEach((m) => {
          if (m.map) m.map.dispose()
          m.dispose()
        })
      }
    })
    scene.clear()
    if (composer) composer.dispose()
    if (skyEnv) skyEnv.dispose()
    renderer.dispose()
    if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement)
  }

  const resolveBuildingKey = (key) => {
    if (!loadedModel || !loadedModel.controls) return null
    const list = loadedModel.controls.buildings.list
    if (list.some((b) => b.key === key)) return key
    const hit = list.find((b) => b.code === String(key))
    return hit ? hit.key : null
  }

  return {
    dispose,
    // --- 视角 ---
    getZoom,
    setZoom,
    getPitch,
    setPitch,
    getRotation: getAzimuth,
    setRotation,
    // 注视点没有"程序化改坐标"的接口：要挪到某栋楼用 flyToCode(楼号)，
    // 拖拽则由右键负责，拖远了用 resetView() 回默认机位。
    flyToCode(code) {
      const hit = labelGroup && labelGroup.children.find((o) => isPlate(o) && o.userData.buildingCode === String(code))
      if (!hit) return false
      selectSprite(hit, { panel: true })
      return true
    },
    resetView() {
      flyAnim = null
      // 复位是用户明确下的机位指令，作废"退回看楼之前"那份快照，否则取消选中会把它拉回去
      viewBeforeFly = null
      controls.target.copy(viewTarget)
      const d = zoomToDistance(DEFAULT_ZOOM, container.clientHeight)
      const a = (DEFAULT_AZIMUTH * Math.PI) / 180
      const p = (DEFAULT_PITCH * Math.PI) / 180
      camera.position.set(
        viewTarget.x + d * Math.cos(p) * Math.sin(a),
        viewTarget.y + d * Math.cos(p) * Math.cos(a),
        viewTarget.z + d * Math.sin(p),
      )
      markDirty()
      emitView()
    },
    // --- 选中 ---
    clearSelection,
    setPanelOpen,
    isPanelOpen: () => panelWanted,
    /** 选中标牌的屏幕位置（每帧出图刷新，非响应式；未选中时 code 为空、visible=false） */
    getSelectionScreen: () => selScreen,
    getSelected: () =>
      selectedSprite
        ? { ...selectedSprite.userData.lla, code: selectedSprite.userData.buildingCode, name: selectedSprite.userData.buildingName }
        : null,
    selectByCode: (code) => {
      const hit = labelGroup && labelGroup.children.find((o) => isPlate(o) && o.userData.buildingCode === String(code))
      if (!hit) return false
      selectSprite(hit, { panel: true })
      return true
    },
    // --- 显隐 ---
    setLabelsVisible(show) {
      labelsWanted = !!show
      updateLabelVisibility()
      emitView()
    },
    /** 周边城市显隐（沿用旧接口名，组件的勾选框不用改） */
    setBuildingsVisible: setContextVisible,
    setContextVisible,
    setCityVisible(v) {
      if (wrapper) wrapper.visible = !!v
      markDirty()
      return wrapper ? wrapper.visible : null
    },
    setEnvVisible(v) {
      if (!envGroup) return null
      envGroup.visible = !!v
      markDirty()
      return envGroup.visible
    },
    getEnvGroup: () => envGroup,
    getEnvBuildings: () => (envGroup && envGroup.userData.envBuildings) || null,
    /**
     * 真实渲染出来的楼体占地盒，每栋一条 [minE, maxE, minN, maxN]（世界系 X=东 / Y=北，米）。
     * 与 renderedBuildingBlockers() 同源 —— 那是"树/环境按真实楼盒避让"用的真值。
     * ⚠️ art/building-layout.json 是**模型校准之前**导出的，位置平均偏西 162m 且各分片各偏，
     * 凡是要按"楼在哪"做判断的离线资产生成器（art/build_context.py 的让位判据），
     * 都应当取这份导出而不是那份布局表。导出方式见 art/README-osm.md。
     */
    getBuildingBoxes: () => renderedBuildingBlockers(),
    /** 同上的带标识版本（含楼号/楼名），导出成 art/building-footprints.json 供生成器用 */
    getBuildingFootprints: () => renderedBuildingBoxesByCode(),
    // --- 驾驶舱：面板内三维详情 + 选中全息 ---
    showBuildingDetail,
    hideBuildingDetail,
    /** 面板槽位尺寸变了（收起/展开动画结束）时重新指认 */
    setDetailSlot: (el) => detail.setSlot(el),
    getDetailState: () => detail.state(),
    // 面板里那栋楼的拖拽轨道：位移是 CSS 像素，由组件把 pointer 事件转过来
    beginDetailOrbit: () => detail.beginOrbit(),
    endDetailOrbit: () => detail.endOrbit(),
    orbitDetailBuilding(dx, dy) {
      const ok = detail.orbitBy(dx, dy)
      if (ok) markDirty()
      return ok
    },
    hasBuildingModel: (code) =>
      !!(loadedModel && loadedModel.controls && loadedModel.controls.buildings.meshesOf(code)),
    /** 调试：某栋楼被拆分出来的几何包围盒（世界米）。核对详情视图取景、也用来验证拆楼是否分对 */
    getBuildingBox: (code) => {
      const src = loadedModel && loadedModel.controls ? loadedModel.controls.buildings.sourceOf(code) : null
      if (!src) return null
      const b = src.box
      return {
        min: [b.min.x, b.min.y, b.min.z].map((v) => +v.toFixed(1)),
        max: [b.max.x, b.max.y, b.max.z].map((v) => +v.toFixed(1)),
        size: [b.max.x - b.min.x, b.max.y - b.min.y, b.max.z - b.min.z].map((v) => +v.toFixed(1)),
        meshes: src.pieces.length,
      }
    },
    // --- 校准/微调 ---
    setCalibration,
    getCalibration: () => ({ ...calib }),
    listParts: () => (loadedModel && loadedModel.controls ? loadedModel.controls.parts.list.slice() : []),
    setPartTransform: (dir, t) => {
      if (!loadedModel || !loadedModel.controls) return false
      markDirty()
      return loadedModel.controls.parts.set(dir, t)
    },
    getPartTransform: (dir) => (loadedModel && loadedModel.controls ? loadedModel.controls.parts.get(dir) : null),
    resetPartTransform: (dir) => {
      if (!loadedModel || !loadedModel.controls) return false
      markDirty()
      return loadedModel.controls.parts.reset(dir)
    },
    getAllPartTransforms: () => (loadedModel && loadedModel.controls ? loadedModel.controls.parts.all() : []),
    setPartVisible: (dir, visible) => {
      if (!loadedModel || !loadedModel.controls) return false
      markDirty()
      return loadedModel.controls.parts.set(dir, { visible: !!visible })
    },
    listBuildings: () => (loadedModel && loadedModel.controls ? loadedModel.controls.buildings.list.slice() : []),
    setBuildingTransform: (key, t) => {
      if (!loadedModel || !loadedModel.controls) return false
      const k = resolveBuildingKey(key)
      if (k) markDirty()
      return k ? loadedModel.controls.buildings.set(k, t) : false
    },
    getBuildingTransform: (key) => {
      if (!loadedModel || !loadedModel.controls) return null
      const k = resolveBuildingKey(key)
      return k ? loadedModel.controls.buildings.get(k) : null
    },
    resetBuildingTransform: (key) => {
      if (!loadedModel || !loadedModel.controls) return false
      const k = resolveBuildingKey(key)
      if (k) markDirty()
      return k ? loadedModel.controls.buildings.reset(k) : false
    },
    getAllBuildingTransforms: () => (loadedModel && loadedModel.controls ? loadedModel.controls.buildings.all() : []),
    setBuildingVisible: (key, visible) => {
      if (!loadedModel || !loadedModel.controls) return false
      const k = resolveBuildingKey(key)
      if (k) markDirty()
      return k ? loadedModel.controls.buildings.set(k, { visible: !!visible }) : false
    },
    // --- 灯光 / 后期 ---
    getLights: () => lights.slice(),
    setLightIntensity: (patch) => {
      if (!patch) return
      lights.forEach((l) => {
        const v = patch[l.name]
        if (v === undefined) return
        if (typeof v === 'number') l.intensity = v
        else {
          if (typeof v.intensity === 'number') l.intensity = v.intensity
          if (typeof v.color === 'number') l.color.setHex(v.color)
        }
      })
      markDirty()
    },
    /**
     * 编号楼自发光强度（A/B 用，不用重载页面）。emissiveIntensity 是 uniform，
     * 改它不触发重编译；emissiveMap 是加载时一次性挂上的（见 loadCityTiles）。
     */
    setFacadeEmissive(strength) {
      const mats = (loadedModel && loadedModel.materials) || []
      if (typeof strength === 'number') mats.forEach((m) => { m.emissiveIntensity = strength })
      markDirty()
      return {
        materials: mats.length,
        strength: mats.length ? +mats[0].emissiveIntensity.toFixed(3) : null,
        hasMap: mats.length ? !!mats[0].emissiveMap : null,
      }
    },
    /** 真阴影开关（A/B 用）。改 renderer.shadowMap.enabled 会换着色器 define，要全量重编译 */
    setShadows(on) {
      const want = !!on
      if (want !== renderer.shadowMap.enabled) {
        renderer.shadowMap.enabled = want
        lights.forEach((l) => {
          if (l.isDirectionalLight && l.name === SHADOWS.caster) l.castShadow = want
        })
        scene.traverse((o) => {
          const ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []
          ms.forEach((m) => { m.needsUpdate = true })
        })
      }
      if (want) renderer.shadowMap.needsUpdate = true
      markDirty()
      return { enabled: renderer.shadowMap.enabled, fit: shadowFit }
    },
    getShadowFit: () => shadowFit,
    getBuildingOccupancy: (step = 4) => {
      const buildings = loadedModel && loadedModel.controls ? loadedModel.controls.buildings : null
      if (!buildings || !city.labelGroup) return []
      city.group.updateMatrixWorld(true)
      const meshes = []
      const codes = []
      const names = {}
      city.labelGroup.children.forEach((o) => {
        const code = o.userData && o.userData.buildingCode
        if (!code) return
        names[String(code)] = o.userData.buildingName || null
        if (codes.indexOf(String(code)) >= 0) return
        codes.push(String(code))
        const src = buildings.sourceOf(code)
        if (src) src.meshes.forEach((m) => { if (meshes.indexOf(m) < 0) meshes.push(m) })
      })
      if (!meshes.length) return []
      const rc = new THREE.Raycaster()
      rc.near = 0
      rc.far = 600
      const down = new THREE.Vector3(0, 0, -1)
      const at = new THREE.Vector3()
      const out = []
      codes.forEach((code) => {
        const src = buildings.sourceOf(code)
        if (!src) return
        const b = src.box
        const n = Math.max(1, Math.ceil((b.max.x - b.min.x) / step))
        const m = Math.max(1, Math.ceil((b.max.y - b.min.y) / step))
        let s = ''
        for (let j = 0; j < m; j++) {
          at.y = b.min.y + (j + 0.5) * step
          for (let i = 0; i < n; i++) {
            at.x = b.min.x + (i + 0.5) * step
            at.z = b.max.z + 20
            rc.set(at, down)
            s += rc.intersectObjects(meshes, false).length ? '1' : '0'
          }
        }
        out.push({ code, name: names[code] || null,
          minE: +b.min.x.toFixed(2), minN: +b.min.y.toFixed(2), nx: n, ny: m, step, cells: s })
      })
      return out
    },
    setBloom(patch) {
      if (!patch || !bloomPass) return null
      if (typeof patch.strength === 'number') bloomPass.strength = patch.strength
      if (typeof patch.radius === 'number') bloomPass.radius = patch.radius
      if (typeof patch.threshold === 'number') bloomPass.threshold = patch.threshold
      markDirty()
      return { strength: bloomPass.strength, radius: bloomPass.radius, threshold: bloomPass.threshold }
    },
    getBloom: () => (bloomPass ? { strength: bloomPass.strength, radius: bloomPass.radius, threshold: bloomPass.threshold } : null),
    setRender(patch) {
      if (!patch) return null
      if (typeof patch.exposure === 'number') renderer.toneMappingExposure = patch.exposure
      if (typeof patch.toneMapping === 'number' && patch.toneMapping !== renderer.toneMapping) {
        renderer.toneMapping = patch.toneMapping
        scene.traverse((o) => {
          const mats = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []
          mats.forEach((m) => {
            m.needsUpdate = true
          })
        })
      }
      if (typeof patch.fog === 'boolean' && FOG_ENABLED) {
        scene.fog = patch.fog ? new THREE.Fog(FOG_COLOR, FOG_NEAR, FOG_FAR) : null
        scene.traverse((o) => {
          const mats = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []
          mats.forEach((m) => {
            m.needsUpdate = true
          })
        })
      }
      markDirty()
      return { exposure: renderer.toneMappingExposure, toneMapping: renderer.toneMapping, webgl2: renderer.capabilities.isWebGL2 }
    },
    // --- 状态与调试 ---
    /** 调试：从世界坐标 (east, north) 上方垂直向下打射线，返回前几个命中对象与材质。 */
    raycastGround(east, north, fromZ = 400) {
      const out = []
      scene.updateMatrixWorld(true)
      let scanned = 0
      let passed = 0
      const rc = new THREE.Raycaster(new THREE.Vector3(east, north, fromZ), new THREE.Vector3(0, 0, -1), 0, fromZ + 50)
      scene.traverse((o) => {
        if (!o.isMesh || !o.geometry) return
        scanned++
        if (!o.geometry.boundingSphere) o.geometry.computeBoundingSphere()
        const bs = o.geometry.boundingSphere.clone()
        if (!bs || bs.radius === Infinity) return
        bs.applyMatrix4(o.matrixWorld)
        const p = new THREE.Vector3(east, north, 0)
        if (p.distanceTo(bs.center) > bs.radius + fromZ) return
        passed++
        const hits = rc.intersectObject(o, false)
        for (const h of hits) {
          if (out.length >= 8) break
          const ms = Array.isArray(o.material) ? o.material : [o.material]
          const m = (h.face && h.face.materialIndex !== undefined && ms[h.face.materialIndex]) || ms[0]
          out.push({
            obj: o.name || o.type,
            mat: m && m.name,
            z: +h.point.z.toFixed(2),
            transparent: !!(m && m.transparent),
            hasMap: !!(m && m.map),
            color: m && m.color ? m.color.getHexString() : null,
          })
        }
      })
      return { hits: out, scanned, passed }
    },
    /** 调试：屏幕像素（CSS，canvas 左上）反投影到 z=0 平面的世界坐标。 */
    debugScreenWorld(px, py) {
      const rect = renderer.domElement.getBoundingClientRect()
      const nd = new THREE.Vector3(((px - rect.left) / rect.width) * 2 - 1, -(((py - rect.top) / rect.height) * 2 - 1), 0.5)
      nd.unproject(camera)
      const dir = nd.sub(camera.position).normalize()
      const t = -camera.position.z / dir.z
      const p = camera.position.clone().addScaledVector(dir, t)
      return { e: +p.x.toFixed(1), n: +p.y.toFixed(1), t: +t.toFixed(0) }
    },
    /** 调试：把某材质改成 THREE.Side 语义的 side（0=Front 1=Back 2=Double）。 */
    debugSetMatSide(matName, side) {
      let n = 0
      scene.traverse((o) => {
        const ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []
        ms.forEach((m) => {
          if (m && m.name === matName) { m.side = side; m.needsUpdate = true; n++ }
        })
      })
      markDirty()
      return n
    },
    /** 调试：把某名字的材质涂成指定色（定位"这块像素是谁"）。 */
    debugPaintMat(matName, hex) {
      let n = 0
      scene.traverse((o) => {
        const ms = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []
        ms.forEach((m) => {
          if (m && m.name === matName && m.color) { m.color.set(hex); m.needsUpdate = true; n++ }
        })
      })
      markDirty()
      return n
    },
    /** 调试：按网格名切换环境层对象显隐（排除法看"白饼"是谁）。 */
    debugSetMeshVisible(name, v) {
      let n = 0
      scene.traverse((o) => {
        if (o.name === name) { o.visible = !!v; n++ }
      })
      markDirty()
      return n
    },
    getState: () => ({
      zoom: +getZoom().toFixed(3),
      pitch: +getPitch().toFixed(1),
      rotation: +getAzimuth().toFixed(1),
      // 注视点（米）：右键拖拽改的就是它，旋转/缩放不会动
      target: [+controls.target.x.toFixed(1), +controls.target.y.toFixed(1)],
      calib: { ...calib },
      context: contextWanted,
      labels: labelsWanted,
    }),
    /**
     * 调试：按顶层分组统计"这一帧这一遍到底画了哪些可绘制对象"，并做一次视锥预判。
     * 用来回答"950 个 draw call 是从谁身上来的""视锥剔除有没有生效"这类问题。
     * ⚠️ 拆出来的楼 subGeometry 共享整片 position 缓冲，它的 boundingSphere 覆盖整个分片，
     * 所以这里 culled 对编号楼基本恒为 0 —— 那正是"剔不掉"的原因。
     */
    drawBreakdown() {
      scene.updateMatrixWorld(true)
      camera.updateMatrixWorld()
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert()
      const frustum = new THREE.Frustum().setFromProjectionMatrix(
        new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse),
      )
      const sph = new THREE.Sphere()
      const buckets = {}
      scene.traverseVisible((o) => {
        if (!o.isMesh && !o.isSprite && !o.isLine && !o.isPoints) return
        const mats = o.material ? (Array.isArray(o.material) ? o.material : [o.material]) : []
        if (!mats.length) return
        let culled = false
        if (o.frustumCulled && o.geometry) {
          if (!o.geometry.boundingSphere) o.geometry.computeBoundingSphere()
          if (o.geometry.boundingSphere) {
            sph.copy(o.geometry.boundingSphere).applyMatrix4(o.matrixWorld)
            culled = !frustum.intersectsSphere(sph)
          }
        }
        let top = o
        while (top.parent && top.parent !== scene) top = top.parent
        const key = (top.name || top.type) + '/' + (o.isInstancedMesh ? 'inst' : o.isSprite ? 'sprite' : o.isLine ? 'line' : 'mesh')
        const b = buckets[key] || (buckets[key] = { objects: 0, drawn: 0, culled: 0, calls: 0, tris: 0 })
        const inst = o.isInstancedMesh ? o.count : 1
        const idx = o.geometry && o.geometry.index
        const trisPer = idx ? idx.count / 3 : o.geometry && o.geometry.attributes.position ? o.geometry.attributes.position.count / 3 : 0
        b.objects++
        if (culled) b.culled++
        else {
          b.drawn++
          b.calls += mats.length
          b.tris += Math.round(trisPer * inst)
        }
      })
      return buckets
    },
    perf() {
      const info = renderer.info.render
      return {
        renders: renderCount,
        lastCostMs: +lastCost.toFixed(2),
        avgCostMs: +avgCost.toFixed(2),
        drawCalls: info.calls,
        triangles: info.triangles,
        webgl2: renderer.capabilities.isWebGL2,
        geometries: renderer.info.memory.geometries,
        textures: renderer.info.memory.textures,
        stages: lastStages,
      }
    },
    /** 调试：场景里到底有多少可绘制对象 / 多少种材质、多少着色器程序 */
    inventory() {
      const mats = new Set()
      let meshes = 0
      let sprites = 0
      let instanced = 0
      let lines = 0
      let hidden = 0
      let verts = 0
      scene.traverse((o) => {
        if (!o.visible) hidden++
        if (o.isInstancedMesh) {
          instanced++
          meshes++
          const c = o.count
          verts += (o.geometry.getAttribute('position')?.count || 0) * c
        } else if (o.isMesh) {
          meshes++
          verts += o.geometry.getAttribute('position')?.count || 0
        } else if (o.isSprite) sprites++
        else if (o.isLine || o.isPoints) lines++
        if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => mats.add(m))
      })
      return {
        meshes,
        sprites,
        lines,
        instancedMeshes: instanced,
        uniqueMaterials: mats.size,
        hiddenNodes: hidden,
        sceneVertices: verts,
        programs: renderer.info.programs.length,
        textures: renderer.info.memory.textures,
        geometries: renderer.info.memory.geometries,
        pixelRatio: renderer.getPixelRatio(),
        drawingBuffer: renderer.domElement.width + 'x' + renderer.domElement.height,
      }
    },
    labelScreens() {
      if (!labelGroup) return []
      const w = container.clientWidth
      const h = container.clientHeight
      return labelGroup.children
        .filter(isPlate)
        .map((o) => {
          _sv.setFromMatrixPosition(o.matrixWorld).project(camera)
          return {
            code: o.userData.buildingCode,
            name: o.userData.buildingName,
            x: Math.round((_sv.x * 0.5 + 0.5) * w),
            y: Math.round((-_sv.y * 0.5 + 0.5) * h),
            onScreen: _sv.z <= 1,
          }
        })
    },
    /** 调试：世界米（东, 北, 上）投到屏幕 CSS 像素，用于核对路名落点 */
    projectWorld(east, north, up = 2.2) {
      const p = new THREE.Vector3(east, north, up).project(camera)
      return {
        x: Math.round((p.x * 0.5 + 0.5) * container.clientWidth),
        y: Math.round((-p.y * 0.5 + 0.5) * container.clientHeight),
        onScreen: p.z <= 1,
      }
    },
    roadLabelScreens() {
      if (!roadLabelGroup) return []
      return roadLabelItems.map((it) => ({
        name: it.name,
        visible: !!it.visible,
        x: Math.round(it.screenX || 0),
        y: Math.round(it.screenY || 0),
        chars: it.chars.length,
        rect: it.screenRect
          ? {
              x0: Math.round(it.screenRect.x0), x1: Math.round(it.screenRect.x1),
              y0: Math.round(it.screenRect.y0), y1: Math.round(it.screenRect.y1),
            }
          : null,
        onScreen: !!it.onScreen,
      }))
    },
    labelMetrics() {
      if (!labelGroup) return { error: '无标牌' }
      const w = container.clientWidth
      const h = container.clientHeight
      const items = labelGroup.children
        .filter(isPlate)
        .map((o) => {
          o.updateWorldMatrix(true, false)
          const pos = new THREE.Vector3().setFromMatrixPosition(o.matrixWorld)
          // ⚠️ 不能用 matrixWorld 的列向量当 sprite 的屏幕轴：sprite 的 quad 永远对齐屏幕，
          // 而它的 matrixWorld 里带着父级那个 rotX(90°)，用它算出来的包围盒是"转过的"，
          // 比实际大出一截（实测中位屏高 44 vs 真实 38），避让检查会被它骗出假重叠。
          const rect = spriteScreenRect(o, pos, w, h)
          const anc = pos.clone().project(camera)
          return {
            code: o.userData.buildingCode,
            name: o.userData.buildingName,
            visible: o.visible,
            anchor: {
              x: Math.round((anc.x * 0.5 + 0.5) * w),
              y: Math.round((-anc.y * 0.5 + 0.5) * h),
              ndcZ: +anc.z.toFixed(2),
            },
            rect,
            screenH: Math.round(rect.y1 - rect.y0),
            screenW: Math.round(rect.x1 - rect.x0),
          }
        })
      if (!items.length) return { count: 0 }
      const hs = items.map((i) => i.screenH).sort((a, b) => a - b)
      // 避让是否真的生效：只统计"两块都还露着却矩形相交"的对数，正常应为 0
      const on = items.filter((i) => i.visible)
      const overlap = []
      for (let i = 0; i < on.length; i++) {
        for (let j = i + 1; j < on.length; j++) {
          const a = on[i].rect
          const b = on[j].rect
          if (!(a.x1 < b.x0 || b.x1 < a.x0 || a.y1 < b.y0 || b.y1 < a.y0)) {
            overlap.push(on[i].code + '/' + on[j].code)
          }
        }
      }
      return {
        count: items.length,
        platesShown: on.length,
        badgesShown: (labelGroup ? labelGroup.children.filter((o) => o.isSprite && o.userData.labelKind === 'badge' && o.visible).length : 0),
        medianScreenH: hs[Math.floor(hs.length / 2)],
        overlap,
        declutter: lastDeclutter,
        lastScale: lastLabelScale,
        roadLabels: {
          total: roadLabelItems.length,
          visible: roadLabelItems.filter((i) => i.visible).length,
          ...lastRoadLabels,
        },
        items,
      }
    },
    getRippleInfo() {
      if (!ripple) return null
      return { active: ripple.active, visible: ripple.group.visible }
    },
    /** 调试：把某栋楼的标牌投到屏幕上，验证"标牌在楼正上方" */
    checkLabels() {
      if (!loadedModel || !loadedModel.controls) return []
      const w = container.clientWidth
      const h = container.clientHeight
      const sprites = labelGroup ? labelGroup.children.filter(isPlate) : []
      return loadedModel.controls.buildings.list
        .map((b) => {
          const world = loadedModel.controls.buildings.worldPosOf(b.key)
          if (!world) return null
          const p = world.clone().project(camera)
          const sp = sprites.find((o) => o.userData.buildingCode === b.code)
          if (!sp) return null
          _sv.setFromMatrixPosition(sp.matrixWorld)
          const lp = _sv.clone().project(camera)
          return {
            name: b.name || b.code,
            building: [Math.round((p.x * 0.5 + 0.5) * w), Math.round((-p.y * 0.5 + 0.5) * h)],
            label: [Math.round((lp.x * 0.5 + 0.5) * w), Math.round((-lp.y * 0.5 + 0.5) * h)],
          }
        })
        .filter(Boolean)
    },
  }
}
